<script setup lang="ts">
/**
 * Stripe's Payment Element: the card number, expiry and security code in one
 * field, plus Apple Pay and Google Pay where the browser offers them.
 *
 * Whoever uses it chooses what the payment is for through `options` — an amount
 * to hold at checkout, or a SetupIntent's client secret to save a card — and
 * confirms with the Stripe instance and Elements handed back on `ready`.
 */
import { onBeforeUnmount, onMounted, ref, useId } from 'vue'
import { Lock } from 'lucide-vue-next'
import { loadStripe } from '@/lib/stripe'

const props = defineProps<{
  stripeKey: string
  options: Record<string, unknown>
  fields?: Record<string, unknown>
}>()

const emit = defineEmits<{
  ready: [stripe: any, elements: any]
  change: [complete: boolean, message: string]
}>()

const elementId = `payment-element-${useId()}`
const isLoading = ref(true)
const loadFailed = ref(false)
const message = ref('')
let paymentElement: any = null

onMounted(async () => {
  try {
    const stripe = await loadStripe(props.stripeKey)
    const elements = stripe.elements({
      appearance: {
        theme: document.documentElement.classList.contains('dark') ? 'night' : 'stripe',
        variables: {
          borderRadius: '12px',
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
        },
      },
      ...props.options,
    })

    paymentElement = elements.create('payment', {
      defaultValues: { billingDetails: { address: { country: 'GB' } } },
      fields: props.fields,
      layout: 'tabs',
      // Note: Link's sign-up asks again for details the page already holds.
      wallets: { link: 'never' },
    })
    paymentElement.on('ready', () => {
      isLoading.value = false
      emit('ready', stripe, elements)
    })
    paymentElement.on('change', (event: any) => {
      message.value = event.error?.message ?? ''
      emit('change', event.complete, message.value)
    })
    paymentElement.on('loaderror', () => {
      isLoading.value = false
      loadFailed.value = true
    })
    paymentElement.mount(`#${elementId}`)
  } catch {
    isLoading.value = false
    loadFailed.value = true
  }
})

onBeforeUnmount(() => paymentElement?.destroy())
</script>

<template>
  <section class="rounded-2xl bg-white p-4 shadow-sm md:p-8">
    <h2 class="mb-6 text-xl font-semibold text-gray-900">Payment details</h2>

    <p v-if="isLoading" class="flex min-h-[120px] items-center justify-center text-sm text-gray-400">
      Loading the payment form…
    </p>

    <p v-if="loadFailed" role="alert" class="rounded-lg bg-red-50 p-3 text-sm text-red-700">
      The payment form could not load. Please refresh the page.
    </p>

    <div :id="elementId" />

    <p v-if="message" role="alert" class="mt-2 text-sm text-red-600">{{ message }}</p>

    <p class="mt-6 flex items-center gap-2 text-xs text-gray-500">
      <Lock class="h-4 w-4" aria-hidden="true" />
      Your details go straight to Stripe, who process our payments; we never see your card number.
    </p>
  </section>
</template>
